import {Component, OnDestroy, OnInit} from '@angular/core';
import {UserState} from '../../api/models/user-state';
import {Subscription} from 'dexie';
import {finalize} from 'rxjs/operators';
import {GlobalApp} from '../../tools/global';
import {Router} from '@angular/router';
import {SubscriptionService} from '../../api/services/subscription.service';
import {MatSelectChange} from '@angular/material/select';
import {ConfirmDialogComponent} from '../../dialogs/confirm-dialog/confirm-dialog.component';
import {DialogResult, DialogResultType} from '../../dialogs/DialogResult';
import {MatDialog} from '@angular/material/dialog';
import {DataOnDemandService} from '../../api/services/data-on-demand.service';
import {DodEndPointEx} from '../../api/models/dod-end-point-ex';
import {DodBatchLayoutEx} from '../../api/models/dod-batch-layout-ex';
import {
  DataOnDemandLayoutDialogComponent,
  DataOnDemandLayoutDialogResult
} from '../../dialogs/data-on-demand-layout-dialog/data-on-demand-layout-dialog.component';
import {UserStateEx} from '../../api/models-ex/user-state-ex';
import {MatSnackBar} from '@angular/material/snack-bar';
import {
  RecurringDodDialogComponent,
  RecurringDodDialogResult,
  RecurringDodType
} from '../../dialogs/recurring-dod-dialog/recurring-dod-dialog.component';
import {DodRecurring} from '../../api/models/dod-recurring';
import {DodJobEx} from '../../api/models/dod-job-ex';
import {DataOnDemandDialogComponent, DataOnDemandDialogResult} from '../../dialogs/data-on-demand-dialog/data-on-demand-dialog.component';
import {DodExportStatus} from '../../api/models/dod-export-status';

@Component({
  selector: "app-data-layouts",
  templateUrl: "./data-layouts.component.html"
})
export class DataLayoutsComponent implements OnInit, OnDestroy {

  userState: UserState = {
    reportOnly: false,
    userManagerOnly: false,
    invoiceManagerOnly: false,
    interactives: [],
    modifiedInteractives: [],
    xmlas: [],
    reportExportState: {},
    isInteractiveUser: false,
    isReportExportUser: false,
    isEmbeddedUser: false,
    hasAiChats: false,
    isValidState: false
  };
  userStateSubscription: Subscription = null;

  public loadingData = true;
  public allEndPoints: DodEndPointEx[] = [];
  public allLayouts: DodBatchLayoutEx[] = [];

  public currentLayouts: LayoutDetails[] = [];
  public exportStatus: DodExportStatus[] = [];

  public refreshTimer: any = null;
  private refreshingJobs = false;
  private notifiedFinishedLayoutIds = new Set<number>();

  // Layouts whose export request is in flight: it can take a moment to be accepted, so the row says so straight
  // away rather than looking like nothing happened (and tempting a second click).
  public startingIds = new Set<number>();
  // Bumped whenever an export is submitted, so a status poll already in flight (which may predate the new job) is
  // ignored rather than overwriting the fresh status with the old one.
  private statusGeneration = 0;
  private idleTicks = 0;

  constructor(
    private app: GlobalApp,
    private router: Router,
    private subscriptionService: SubscriptionService,
    private dodService: DataOnDemandService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    this.app.getAuthorizationStatus().subscribe((result) => {
      if (!result.isValid) {
        this.app.logOut();
        this.router.navigate(["login"]);
      } else {
        this.app.initialRefreshUserState(this.subscriptionService);
      }
    });
  }

  ngOnInit() {

    this.userStateSubscription = this.app.userState.subscribe((userState) => {
      this.userState = userState;
    });

    this.dodService.dataOnDemandGetUserAvailableDodEndPoints().subscribe(dodEndPoints => {
      this.allEndPoints = dodEndPoints.sort((left, right) => left.sortOrder - right.sortOrder);
    });

    // Don't start another status request until the previous one has completed, or slow responses pile up.
    // Poll quickly while an export is running, and slowly otherwise.
    this.refreshTimer = setInterval(() => {
      this.idleTicks++;
      if (!this.refreshingJobs && (this.hasRunningExport || this.idleTicks >= 3)) {
        this.idleTicks = 0;
        this.refreshJobs();
      }
    }, 2000);

    this.loadLayouts();
  }

  ngOnDestroy() {
    if (this.userStateSubscription) {
      this.userStateSubscription.unsubscribe();
    }

    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  }

  loadLayouts() {
    this.loadingData = true;
    this.dodService.dataOnDemandGetUserAvailableDodLayouts().subscribe(dodLayouts => {
      this.allLayouts = dodLayouts;
      this.refreshLayouts();
      this.loadingData = false;
    });
  }

  get hasRunningExport(): boolean {
    return this.startingIds.size > 0 || this.exportStatus.some(x => !x.finishTime && !!x.lastStatus && x.lastStatus !== 'Never Ran' && !this.isErrorStatus(x));
  }

  private isErrorStatus(status: DodExportStatus): boolean {
    return !!status.lastStatus && status.lastStatus.startsWith('Error');
  }

  refreshJobs(onLoaded?: () => void) {
    let layoutIds = this.currentLayouts.map(x => x.dodBatchLayoutId);
    const generation = this.statusGeneration;
    this.refreshingJobs = true;
    this.dodService.dataOnDemandGetLatestLayoutJobStatus(layoutIds).pipe(
      finalize(() => this.refreshingJobs = false)
    ).subscribe(latestExports => {
      if (generation === this.statusGeneration) {
        this.exportStatus = latestExports;
        this.updateJobs();
      }
      if (onLoaded) onLoaded();
    }, () => {
      if (onLoaded) onLoaded();
    });
  }

  refreshLayouts() {
    // Keep each row's current status while the new one loads, rather than flashing "Never Ran".
    const previous = new Map(this.currentLayouts.map(x => [x.dodBatchLayoutId, x.exportStatus] as [number, string]));
    this.currentLayouts = this.allLayouts.map(x => {
      return {
        ...x,
        exportStatus: previous.get(x.dodBatchLayoutId) ?? 'Never Ran'
      }
    });
    this.refreshJobs();
  }

  updateJobs() {
    for(let i = 0; i < this.currentLayouts.length; i++) {
      if (this.startingIds.has(this.currentLayouts[i].dodBatchLayoutId)) {
        this.currentLayouts[i].exportStatus = this.startingText;
        continue;
      }
      let found = false;
      for(let e = 0; e < this.exportStatus.length; e++) {
        if (this.currentLayouts[i].dodBatchLayoutId == this.exportStatus[e].dodLayoutId) {
          found = true;
          this.currentLayouts[i].exportStatus = this.getExportStatus(this.exportStatus[e]);
          this.notifyIfNewlyFinished(this.exportStatus[e]);
        }
      }
      if (!found) {
        this.currentLayouts[i].exportStatus = 'Never Ran';
      }
    }
  }

  // Export destinations (like AI Chat file uploads) are reflected in the cached UserState,
  // which is only refetched on demand - so a completed export needs to explicitly trigger
  // a refresh, otherwise the new file won't show up until an unrelated refresh happens to fire.
  private notifyIfNewlyFinished(status: DodExportStatus) {
    if (!status.finishTime || this.notifiedFinishedLayoutIds.has(status.dodLayoutId)) return;
    this.notifiedFinishedLayoutIds.add(status.dodLayoutId);
    this.app.refreshUserState(this.subscriptionService);
  }

  // Users with no active subscription cannot create, edit, schedule or run exports.
  get hasActiveSubscription(): boolean {
    return this.allEndPoints.some(x => x.subHeaders && x.subHeaders.length > 0);
  }

  createLayout() {
    if (!this.hasActiveSubscription) return false;

    let subscriptionId = -1;
    if (this.allEndPoints.length > 0 && this.allEndPoints[0].subHeaders && this.allEndPoints[0].subHeaders.length > 0) {
      subscriptionId = this.allEndPoints[0].subHeaders[0].subscriptionId;
    }

    let layout : DodBatchLayoutEx = {
      subscriptionId: subscriptionId,
      allowEditing: true,
      subscriptionExpired: false,
      dodBatchLayoutId: 0,
      preFilterId: -1,
      directFilterData: undefined,
      isOwner: true,
      userId: 0,
      fileTypeId: 0, // Default to CSV
      deleted: false,
      destinationId: 0,
      notificationEmail: localStorage.getItem("emailAddress"),
      emailConfig: { emailAddress: localStorage.getItem("emailAddress") },
      endPoints: []
    };

    DataOnDemandLayoutDialogComponent.showDialog(
      this.dialog,
      this.allEndPoints,
      layout,
      UserStateEx.getDodLayoutShareState(this.userState, layout),
      this.userState.preFilters,
      true,
      this.userState
    ).subscribe(result => {
      if (result.ResultType === DialogResultType.Ok) {
        this.app.pushLoading("Saving, Please Wait...");
        let resultData = result.ResultData as DataOnDemandLayoutDialogResult;
        this.dodService.dataOnDemandCreateDataOnDemandBatchLayout(resultData.dodLayout).subscribe(dodLayoutId => {
          this.dodService.dataOnDemandUpdateDodBatchLayoutSharing({dodBatchLayoutId: dodLayoutId, updateSharingStatus: UserStateEx.getSharingStatus(resultData.shareState)}).subscribe(result => {
            this.app.popLoading();
            this.loadLayouts();
            /*this.snackBar.open("Layout '" + resultData.dodLayout.name + "' Created.", null, {
              duration: 5000
            });*/
          });
        });
      }
    })

    return false;
  }

  editLayout(layout: DodBatchLayoutEx) {
    if (layout.subscriptionExpired) return false;

    DataOnDemandLayoutDialogComponent.showDialog(
      this.dialog,
      this.allEndPoints,
      layout,
      UserStateEx.getDodLayoutShareState(this.userState, layout),
      this.userState.preFilters,
      true,
      this.userState
    ).subscribe(result => {
      if (result.ResultType === DialogResultType.Ok) {
        this.app.pushLoading("Saving, Please Wait...");
        let resultData = result.ResultData as DataOnDemandLayoutDialogResult;
        this.dodService.dataOnDemandUpdateDataOnDemandBatchLayout({dataOnDemandBatchLayoutId: layout.dodBatchLayoutId, request: resultData.dodLayout}).subscribe(dodLayoutId => {
          this.dodService.dataOnDemandUpdateDodBatchLayoutSharing({dodBatchLayoutId: layout.dodBatchLayoutId, updateSharingStatus: UserStateEx.getSharingStatus(resultData.shareState)}).subscribe(result => {
            this.app.popLoading();
            this.loadLayouts();
          });
        });
      }
    })

    return false;
  }

  deleteLayout(layout: DodBatchLayoutEx) {
    ConfirmDialogComponent.showDialog(this.dialog, "Confirm Delete", "Are you sure you want to delete this layout?")
      .subscribe(result => {
        if (result.ResultType == DialogResultType.Ok) {
          this.app.pushLoading("Deleting, Please Wait...");
          this.dodService.dataOnDemandDeleteDataOnDemandBatchLayout(layout.dodBatchLayoutId).subscribe(_ => {
            this.app.refreshUserState(this.subscriptionService, () => {
              this.app.popLoading();
              this.loadLayouts();
            });
          })
        }
      });

    return false;
  }

  createRecurring(layout: DodBatchLayoutEx) {
    if (layout.subscriptionExpired) return false;

    RecurringDodDialogComponent.showDialog(
      this.dialog,
    -1,
    layout.dodBatchLayoutId,
    RecurringDodType.Weekly,
      0,
      1
    ).subscribe((result) => {
      if(result.ResultType == DialogResultType.Ok) {
        this.app.pushLoading("Saving, Please Wait...");
        const results: RecurringDodDialogResult = result.ResultData;
        this.dodService.dataOnDemandCreateRecurringDod({
          dodRecurringId: -1,
          dodBatchLayoutId: results.dodBatchLayoutId,
          recurringType: results.recurringType,
          dayOfWeek: results.dayOfWeek,
          dayOfMonth: results.dayOfMonth
        }).subscribe((result) => {
          this.app.popLoading();
          this.loadLayouts();
          /*this.snackBar.open("Scheduled Export Saved.", null, {
            duration: 5000
          });*/
        });
      }
    });
    return false;
  }

  editRecurring(recurring: DodRecurring) {
    if (this.currentLayouts.some(x => x.dodBatchLayoutId === recurring.dodBatchLayoutId && x.subscriptionExpired)) return false;

    RecurringDodDialogComponent.showDialog(
      this.dialog,
      recurring.dodRecurringId,
      recurring.dodBatchLayoutId,
      recurring.recurringType,
      recurring.dayOfWeek,
      recurring.dayOfMonth
    ).subscribe((result) => {
      if(result.ResultType == DialogResultType.Ok) {
        this.app.pushLoading("Saving, Please Wait...");
        const results: RecurringDodDialogResult = result.ResultData;
        this.dodService.dataOnDemandUpdateRecurringDod({
          dodRecurringId: results.dodRecurringId,
          request: {
            dodRecurringId: results.dodRecurringId,
            dodBatchLayoutId: results.dodBatchLayoutId,
            recurringType: results.recurringType,
            dayOfWeek: results.dayOfWeek,
            dayOfMonth: results.dayOfMonth
          }
        }).subscribe((result) => {
          this.app.popLoading();
          this.loadLayouts();
          /*this.snackBar.open("Scheduled Export Updated.", null, {
            duration: 5000
          });*/
        });
      }
    });
    return false;
  }

  deleteRecurring(recurring: DodRecurring) {
    ConfirmDialogComponent.showDialog(
      this.dialog,
      "Confirm Delete",
      "Are you sure you want to delete this recurring export?"
    ).subscribe((result) => {
      if (result.ResultType == DialogResultType.Ok) {
        this.app.pushLoading("Deleting, Please Wait...");
        this.dodService.dataOnDemandDeleteRecurringDod(recurring.dodRecurringId).subscribe((result) => {
          this.app.popLoading();
          this.loadLayouts();
          /*this.snackBar.open("Recurring Export Deleted.", null, {
            duration: 5000
          });*/
        });
      }
    });
    return false;
  }

  exportNow(layout: DodBatchLayoutEx) {
    if (layout.subscriptionExpired) return false;
    if (this.startingIds.has(layout.dodBatchLayoutId)) {
      this.snackBar.open("Export already starting", null, {duration: 3000});
      return false;
    }

    let fileType = layout.fileTypeId;

    const availableEndPoints = layout.endPoints.map(ep => {
      const details = this.allEndPoints.find(x => x.dodEndPointId === ep.dodEndPointId);
      return {
        id: ep.dodEndPointId,
        title: details ? details.title : 'Unknown'
      };
    });

    DataOnDemandDialogComponent.showDialog(this.dialog, fileType, layout.notificationEmail, layout.destinationId, layout.azureConfig, layout.awsConfig, layout.sftpConfig, layout.emailConfig, availableEndPoints, layout.preFilterId != null).subscribe(result => {
      if (result.ResultType !== DialogResultType.Ok) return;
      const resultData = result.ResultData as DataOnDemandDialogResult;
      const layoutId = layout.dodBatchLayoutId;
      this.setStarting(layoutId, true);
      this.snackBar.open("Starting Export...", null, {duration: 3000});
      this.dodService.dataOnDemandExportOnDemand({
        dodLayoutId: layoutId,
        fileTypeId: resultData.fileType,
        destinationId: resultData.destinationId,
        azureConfig: resultData.azureConfig,
        awsConfig: resultData.awsConfig,
        sftpConfig: resultData.sftpConfig,
        emailConfig: resultData.emailConfig,
        notificationEmail: resultData.notificationEmail,
        selectedEndPointIds: resultData.selectedEndPointIds
      }).subscribe({
        next: () => {
          // The job exists as soon as the request returns, so the next status fetch is the real one. Keep showing
          // "Starting" until it arrives, and drop any poll that was already in flight.
          this.statusGeneration++;
          this.refreshJobs(() => this.setStarting(layoutId, false));
          this.snackBar.open("Export Submitted.", null, {duration: 5000});
        },
        error: (error) => {
          this.setStarting(layoutId, false);
          const message = (error && (error.error || error.message)) || "Could not start the export";
          this.snackBar.open(typeof message === "string" ? message : "Could not start the export", null, {duration: 8000});
        }
      });
    })

    return false;
  }

  private readonly startingText = 'Starting export...';

  private setStarting(layoutId: number, starting: boolean) {
    if (starting) this.startingIds.add(layoutId); else this.startingIds.delete(layoutId);
    const layout = this.currentLayouts.find(x => x.dodBatchLayoutId === layoutId);
    if (!layout) return;
    if (starting) {
      layout.exportStatus = this.startingText;
    } else {
      this.updateJobs();
    }
  }

  isErrorText(text: string | undefined): boolean {
    return !!text && text.startsWith('Error');
  }

  getExportStatus(status: DodExportStatus) : string {
    // A failed export has a finish time too, but must not read as "Finished".
    if (this.isErrorStatus(status)) {
      return status.lastStatus;
    } else if (status.finishTime) {
      return 'Finished: ' + (new Date(status.finishTime + 'Z')).toLocaleString();
    } else {
      return status.lastStatus;
    }
  }
}

export interface LayoutDetails extends DodBatchLayoutEx
{
  exportStatus: string | undefined;
}
